import type {
  Treatment,
  TreatmentDTO,
  TreatmentTemplateDetails,
  TreatmentTemplateDetailsDTO,
  HappinessRating,
  PlannedTreatment,
  PlannedRoutine,
  PlannedSession,
  PlannedTreatmentResponse,
  PlannedTreatmentResponseDTO,
  QuestionnaireTriggerCondition,
} from "@akina/domain";
import { toCamelCase } from "@akina/utils";
import type { CMSStory } from "../adapters";
import type {
  ExerciseInsight,
  ExerciseInsightApi,
  TasksSummariesDTO,
  TasksSummariesDTOApi,
  TreatmentProgressResponse,
  TreatmentProgressResponseApi,
} from "types/schemas";
import { ApiMethod, type ApiConfiguration } from "..";
import {
  plannedTreatmentMapper,
  plannedSessionMapper,
  plannedRoutineMapper,
  treatmentMapper,
  plannedTreatmentResponseMapper,
} from "../mappers";
import { uploadFileToS3 } from "./shared";

/*eslint-disable max-lines-per-function*/
export const treatmentApi = (config: ApiConfiguration) => ({
  async getProgressData(treatmentId: string): Promise<TreatmentProgressResponse> {
    const result = await config.httpService.fetch<TreatmentProgressResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.progressData(treatmentId),
    });

    return toCamelCase(result);
  },

  async getActiveTreatment(): Promise<Treatment | null> {
    const result = await config.httpService.fetch<TreatmentDTO | null>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getActive,
    });

    if (!result) return null;

    return treatmentMapper.toDomain(result);
  },
  async getTreatments(): Promise<Treatment[]> {
    const result = await config.httpService.fetch<TreatmentDTO[]>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.all,
    });

    return result.map(treatmentMapper.toDomain);
  },
  async updateTreatmentClinicTherapist(params: {
    therapistId?: string;
    clinicId: string;
    treatmentId: string;
  }): Promise<Treatment | null> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updateTreatmentClinicTherapist,
      data: {
        therapist_id: params.therapistId,
        organization_id: params.clinicId,
        treatment_id: params.treatmentId,
      },
    });

    return await this.getActiveTreatment();
  },
  async updateTreatmentTherapist(params: { therapistId: string; treatmentId: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updateTreatmentTherapist,
      data: {
        therapist_id: params.therapistId,
        treatment_id: params.treatmentId,
      },
    });
  },
  async updatePrescription(treatmentId: string, newPrescription: File): Promise<void> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.uploadPrescription(treatmentId),
      data: { file_type: newPrescription.type },
    });

    await uploadFileToS3(result.presigned_url, newPrescription);
    await this.getActiveTreatment();
  },
  async getPrescriptionUrl(treatmentId: string): Promise<string> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getPrescriptionUrl(treatmentId),
    });

    return result.presigned_url;
  },
  async TasksSummaries(therapistId: string): Promise<TasksSummariesDTO> {
    const result = await config.httpService.fetch<TasksSummariesDTOApi>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.tasksSummaries(therapistId),
    });

    return toCamelCase(result);
  },
  async getTreatmentTemplateDetails(key: string | undefined): Promise<TreatmentTemplateDetails | undefined> {
    if (!key) return undefined;
    const response = await config.cmsService.get<TreatmentTemplateDetailsDTO>(
      config.cmsEndpoints.treatmentTemplate(key),
    );

    const story = response.data.stories[0];

    if (!story) return undefined;

    return {
      bodyPart: story.content.BodyPart,
      key: story.content.key,
      name: story.content.name,
      description: story.content.Description,
      phases: story.content.phases?.map((p: { key: string; goal: string; name: string }) => ({
        key: p.key,
        goal: p.goal,
        name: p.name,
      })),
    };
  },
  async getTreatmentTemplatesDetails(): Promise<TreatmentTemplateDetails[]> {
    const response = await config.cmsService.get<TreatmentTemplateDetailsDTO>(config.cmsEndpoints.treatmentTemplates);

    return response.data.stories.map((s: CMSStory<TreatmentTemplateDetailsDTO>) => ({
      bodyPart: s.content.BodyPart,
      key: s.content.key,
      name: s.content.name,
      description: s.content.Description,
      phases: s.content.phases?.map((p: { key: string; goal: string; name: string }) => ({
        key: p.key,
        goal: p.goal,
        name: p.name,
      })),
    }));
  },
  async updateGoal(params: { treatmentId: string; goal: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.editGoal,
      data: {
        treatment_id: params.treatmentId,
        goal: params.goal,
      },
    });

    return params.treatmentId;
  },
  async updateDiagnosis(params: { treatmentId: string; diagnosis: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.editDiagnosis,
      data: {
        treatment_id: params.treatmentId,
        diagnosis: params.diagnosis,
      },
    });
  },
  async updatePhase(params: { treatmentId: string; targetPhaseKey: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePhase,
      data: {
        treatment_id: params.treatmentId,
        target_phase_key: params.targetPhaseKey,
      },
    });

    return params.treatmentId;
  },
  async disconnect(params: { treatmentId: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.disconnect,
      data: {
        treatment_id: params.treatmentId,
      },
    });

    return params.treatmentId;
  },
  async submitHappinessFeedback({
    rating,
    comments,
    treatmentId,
    triggerCondition,
  }: {
    rating: HappinessRating;
    comments: string;
    treatmentId: string;
    triggerCondition: QuestionnaireTriggerCondition;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.treatments.happinessFeedback(treatmentId),
      data: {
        training_experience: rating,
        comments: comments,
        trigger_condition: triggerCondition,
      },
    });
  },
  async getDisplayHappinessQuestionnaire({
    treatmentId,
  }: {
    treatmentId: string;
  }): Promise<{ triggerQuestionnaire: boolean; triggerCondition: QuestionnaireTriggerCondition }> {
    const result = await config.httpService.fetch<{
      trigger_questionnaire: boolean;
      trigger_condition: QuestionnaireTriggerCondition;
    }>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.displayHappinessQuestionnaire(treatmentId),
    });

    return {
      triggerQuestionnaire: result.trigger_questionnaire,
      triggerCondition: result.trigger_condition,
    };
  },
  async extendTreatment({
    treatmentId,
    newPrescription,
  }: {
    treatmentId: string;
    newPrescription?: File;
  }): Promise<void> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.extendTreatment,
      data: {
        treatment_id: treatmentId,
        prescription_mime_type: newPrescription ? newPrescription.type : null,
      },
    });
    if (newPrescription) {
      await uploadFileToS3(result.presigned_url, newPrescription);
    }
  },
  async getExerciseInsights(treatmentId: string): Promise<ExerciseInsight[]> {
    const result = await config.httpService.fetch<ExerciseInsightApi[]>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.exerciseInsights(treatmentId),
    });
    return toCamelCase(result);
  },

  async getPlannedTreatment(treatmentId: string): Promise<PlannedTreatmentResponse> {
    const result = await config.httpService.fetch<PlannedTreatmentResponseDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getPlannedTreatment(treatmentId),
    });
    return plannedTreatmentResponseMapper.toDomain(result);
  },

  async updatePlannedTreatment(plannedTreatment: PlannedTreatment) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePlannedTreatment,
      data: plannedTreatmentMapper.toDTO(plannedTreatment),
    });
  },

  async updatePlannedRoutine(plannedRoutine: PlannedRoutine) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePlannedRoutine,
      data: plannedRoutineMapper.toDTO(plannedRoutine),
    });
  },

  async updatePlannedSession(plannedSession: PlannedSession) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePlannedSession,
      data: plannedSessionMapper.toDTO(plannedSession),
    });
  },

  async resetPlannedTreatment(treatmentId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.resetPlannedTreatment,
      data: { treatmentId },
    });
  },

  async resetPlannedSession(sessionId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.resetPlannedSession,
      data: { sessionId },
    });
  },

  async resetPlannedRoutine(routineId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.resetPlannedRoutine,
      data: { routineId },
    });
  },

  async activatePlannedSession(sessionId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.activatePlannedSession,
      data: { sessionId },
    });
  },

  async activatePlannedRoutine(routineId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.activatePlannedRoutine,
      data: { routineId },
    });
  },

  async deactivatePlannedSession(sessionId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.deactivatePlannedSession,
      data: { sessionId },
    });
  },

  async deactivatePlannedRoutine(routineId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.deactivatePlannedRoutine,
      data: { routineId },
    });
  },
});
